import { useState, useEffect, useCallback } from 'react';

interface DeviceCodeResponse {
  device_code: string;
  user_code: string;
  verification_uri: string;
  verification_uri_complete: string;
  expires_in: number;
  interval: number;
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

interface DeviceAuthState {
  isLoading: boolean;
  deviceCode: string | null;
  userCode: string | null;
  verificationUri: string | null;
  verificationUriComplete: string | null;
  accessToken: string | null;
  error: string | null;
  expiresIn: number | null;
  isPolling: boolean;
}

const QA_BASE_URL = 'http://localhost:8787';

export function useDeviceAuth() {
  const [state, setState] = useState<DeviceAuthState>({
    isLoading: false,
    deviceCode: null,
    userCode: null,
    verificationUri: null,
    verificationUriComplete: null,
    accessToken: null,
    error: null,
    expiresIn: null,
    isPolling: false,
  });

  // Start device authorization flow
  const startDeviceAuth = useCallback(async (clientId: string = 'ui-client') => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${QA_BASE_URL}/device/code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ client_id: clientId }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to start device authorization');
      }

      const data: DeviceCodeResponse = result.data;

      setState(prev => ({
        ...prev,
        isLoading: false,
        deviceCode: data.device_code,
        userCode: data.user_code,
        verificationUri: data.verification_uri,
        verificationUriComplete: data.verification_uri_complete,
        expiresIn: data.expires_in,
      }));

      return data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
      throw error;
    }
  }, []);

  // Poll for token with long polling support
  const pollForToken = useCallback(async (deviceCode: string, clientId: string = 'ui-client') => {
    if (state.isPolling) return;

    setState(prev => ({ ...prev, isPolling: true, error: null }));

    try {
      // The server now implements long polling with 30s timeout
      // So we don't need to poll as frequently on the client side
      const response = await fetch(`${QA_BASE_URL}/device/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
          device_code: deviceCode,
          client_id: clientId,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        const errorCode = result.error?.code;
        
        if (errorCode === 'expired_token') {
          throw new Error('Device code has expired. Please start over.');
        } else if (errorCode === 'access_denied') {
          throw new Error('Authorization was denied.');
        } else {
          throw new Error(result.error?.message || 'Failed to get token');
        }
      }

      // Check if the response indicates pending authorization
      if (result.data?.status === 'pending') {
        // Server has waited 30s and still pending, continue polling
        setState(prev => ({ ...prev, isPolling: false }));
        return { pending: true };
      }

      const data: TokenResponse = result.data;

      // Store token in localStorage
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('token_expires_at', (Date.now() + data.expires_in * 1000).toString());

      setState(prev => ({
        ...prev,
        isPolling: false,
        accessToken: data.access_token,
      }));

      return { success: true, token: data.access_token };
    } catch (error) {
      setState(prev => ({
        ...prev,
        isPolling: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
      throw error;
    }
  }, [state.isPolling]);

  // Start polling with interval (optimized for long polling)
  const startPolling = useCallback((deviceCode: string, clientId: string = 'ui-client', interval: number = 32) => {
    // Since server now does long polling with 30s timeout, we can use a longer interval
    // Default to 32 seconds to allow for server processing time
    const pollInterval = setInterval(async () => {
      try {
        const result = await pollForToken(deviceCode, clientId);
        if (result?.success) {
          clearInterval(pollInterval);
        }
      } catch (error) {
        clearInterval(pollInterval);
        console.error('Polling error:', error);
      }
    }, interval * 1000);

    return () => clearInterval(pollInterval);
  }, [pollForToken]);

  // Start continuous polling (for immediate response)
  const startContinuousPolling = useCallback(async (deviceCode: string, clientId: string = 'ui-client') => {
    let shouldContinue = true;
    
    const stopPolling = () => {
      shouldContinue = false;
    };

    const poll = async () => {
      while (shouldContinue) {
        try {
          const result = await pollForToken(deviceCode, clientId);
          if (result?.success) {
            break; // Token received, stop polling
          }
          // If still pending, the server has already waited 30s, so we can immediately retry
        } catch (error) {
          console.error('Continuous polling error:', error);
          break; // Stop on error
        }
      }
    };

    // Start polling
    poll();

    return { stopPolling };
  }, [pollForToken]);

  // Reset state
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      deviceCode: null,
      userCode: null,
      verificationUri: null,
      verificationUriComplete: null,
      accessToken: null,
      error: null,
      expiresIn: null,
      isPolling: false,
    });
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_expires_at');
  }, []);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const expiresAt = localStorage.getItem('token_expires_at');
    
    if (token && expiresAt && Date.now() < parseInt(expiresAt)) {
      setState(prev => ({ ...prev, accessToken: token }));
    } else if (token) {
      // Token expired, clean up
      localStorage.removeItem('access_token');
      localStorage.removeItem('token_expires_at');
    }
  }, []);

  return {
    ...state,
    startDeviceAuth,
    pollForToken,
    startPolling,
    startContinuousPolling,
    reset,
    isAuthenticated: !!state.accessToken,
  };
}